import { WebHelpers } from "./helpers";

export interface OneTrustEvent {
  detail: string[];
}

export type Attitude =
  | "ignore"
  | "reject"
  | "accepted specific"
  | "accepted all"
  | "unknown";

export type ConsentOptions = {
  essential: boolean;
  performance: boolean;
  functional: boolean;
  targeting: boolean;
};

export class ConsentSettings extends WebHelpers {
  has_started = false;
  essential = true;
  performance = true;
  functional = true;
  targeting = true;
  onetrust_consent_on_page_last?: string;
  onetrust_consent_on_page_now?: string;

  set_from_onetrust_event(event: OneTrustEvent): void { // eslint-disable-line
    if (event.length > 0) {
      this.essential = true;
      this.performance = event.indexOf("C0002") >= 0;
      this.functional = event.indexOf("C0003") >= 0;
      this.targeting = event.indexOf("C0004") >= 0;
    } else {
      const msg = "event did not contain consent data for onetrust consent";
      console.error(msg);
      throw new Error(msg);
    }
  }

  set_attitude(attitude: Attitude): void {
    if (attitude != this.get_attitude()) {
      this.set_cookie("att", attitude, 365);
      console.log(`att set to ${attitude}`);
    }
  }

  get_attitude(): Attitude {
    return this.get_cookie("att") as Attitude;
  }

  get_onetrust_cookie(): ConsentOptions | undefined {
    const onetrust_cookie = this.get_cookie("OptanonConsent");
    if (onetrust_cookie) {
      return {
        essential: true,
        performance: (onetrust_cookie?.search("C0002:1") || 1) >= 0,
        functional: (onetrust_cookie?.search("C0003:1") || 1) >= 0,
        targeting: (onetrust_cookie?.search("C0004:1") || 1) >= 0
      };
    } else {
      return undefined;
    }
  }

  set_from_onetrust_cookie(): void {
    const onetrust_cookie = this.get_onetrust_cookie();
    console.log(
      "Setting consent preferences from OneTrust cookie: ",
      onetrust_cookie
    );
    if (onetrust_cookie) {
      this.essential = true;
      this.performance = onetrust_cookie.performance;
      this.functional = onetrust_cookie.functional;
      this.targeting = onetrust_cookie.targeting;
    } else {
      const msg = "could not access/find cookie for onetrust consent";
      // console.error(msg);
      throw new Error(msg);
    }
  }

  get_consent(exclude_attitude?: boolean): {
    essential: boolean;
    functional: boolean;
    performance: boolean;
    targeting: boolean;
    attitude?: Attitude;
  } {
    const base = {
      essential: this.essential,
      functional: this.functional,
      performance: this.performance,
      targeting: this.targeting
    };

    if (exclude_attitude) {
      return base;
    } else {
      return {
        ...base,
        ...{ attitude: this.get_attitude() }
      };
    }
  }

  get_consent_json(exclude_attitude?: boolean): string {
    return JSON.stringify(this.get_consent(exclude_attitude));
  }

  consent_has_changed_since_initial_pageload(): boolean {
    this.onetrust_consent_on_page_now = this.get_consent_json(true);
    if (
      !this.onetrust_consent_on_page_last ||
      this.onetrust_consent_on_page_last != this.onetrust_consent_on_page_now
    ) {
      // Reload the page to ensure the correct tags are loaded
      if (!this.onetrust_consent_on_page_last) {
        // No need to refresh if the settings were not explicitly defined initially
        // The default setting will be set automatically, and the user can change that
        this.onetrust_consent_on_page_last = this.onetrust_consent_on_page_now;
      } else {
        // now the user has made a change, we need to reload to make sure the right
        // tags are or are not loaded
        console.log("now " + this.onetrust_consent_on_page_now);
        console.log("last " + this.onetrust_consent_on_page_last);
        return true;
      }
    }
    return false;
  }
}
